import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { readersOfMessage } from '@d-orca/shared/client/utils';
import { useMessageReceipts } from '@d-orca/shared/client/hooks';
import { Modal } from '@components/ui/Modal/Modal';
import { Avatar } from '@components/ui/Avatar/Avatar';
import { CheckCircleFilledIcon } from '@utils/icons';
import type { CommsMember, CommsMessage } from '@interfaces/ICommsApi';
import { memberName } from '../../utils/memberName';
import info from '../infoDialog.module.css';
import styles from './MessageInfoDialog.module.css';

interface IMessageInfoDialogProps {
  siteId: string;
  /** The message whose record is open; null closes the dialog. */
  message: CommsMessage | null;
  /** The project roster the audience's ids resolve against. */
  members: CommsMember[];
  onClose: () => void;
}

/**
 * The record behind one of your own messages: who it was for, and who has
 * read it, with when. Names come from the roster; an id the roster does not
 * know (someone since removed from the project) is shown as the id.
 */
export function MessageInfoDialog({ siteId, message, members, onClose }: IMessageInfoDialogProps) {
  const { t, i18n } = useTranslation();
  const query = useMessageReceipts(siteId, message?.channelId ?? null, message?.id ?? null);
  const nameOf = useMemo(() => {
    const byId = new Map(members.map((m) => [m.id, memberName(m)]));
    return (id: string) => byId.get(id) ?? id;
  }, [members]);
  const timeFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }),
    [i18n.language],
  );

  const record = query.data;
  const state = record && message ? readersOfMessage(record.audience, record.receipts, message.authorId) : null;

  return (
    <Modal open={message !== null} onClose={onClose} title={t('comms.receipts.infoTitle')} size="sm">
      {query.isPending && <p className={info.muted}>{t('comms.receipts.loading')}</p>}
      {query.isError && <p className={info.muted}>{t('comms.receipts.error')}</p>}
      {state && state.read.length === 0 && state.noEvidence.length === 0 && (
        <p className={info.muted}>{t('comms.receipts.noOneElse')}</p>
      )}
      {state && state.read.length > 0 && (
        <section className={styles.group}>
          <h3 className={info.sectionTitle}>{t('comms.receipts.readGroup')} · {state.read.length}</h3>
          <ul className={`${info.list} ${styles.list}`}>
            {state.read.map((r) => (
              <li key={r.userId} className={styles.row}>
                <Avatar name={nameOf(r.userId)} size={24} tinted />
                <span className={info.name}>{nameOf(r.userId)}</span>
                <time className={styles.when} dateTime={r.readAt}>{timeFmt.format(new Date(r.readAt))}</time>
                <span className={`${styles.mark} ${styles.markRead}`} aria-hidden="true"><CheckCircleFilledIcon size={13} /></span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {state && state.noEvidence.length > 0 && (
        <section className={styles.group}>
          <h3 className={info.sectionTitle}>{t('comms.receipts.unreadGroup')} · {state.noEvidence.length}</h3>
          <ul className={`${info.list} ${styles.list}`}>
            {state.noEvidence.map((id) => (
              <li key={id} className={styles.row}>
                <Avatar name={nameOf(id)} size={24} tinted />
                <span className={info.name}>{nameOf(id)}</span>
                <span className={`${styles.mark} ${styles.markUnread}`} aria-hidden="true"><CheckCircleFilledIcon size={13} /></span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Modal>
  );
}
